var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Header.tsx
import { useState, useCallback, useEffect, useContext, } from 'react';
import '../styles/header.scss';
import ButtonGroup from './ButtonGroup';
import CustomScrollbar from './CustomScrollbar';
import globalStore from '../store';
import NavBtn from './NavBtn';
import { DataContext } from '../player/DataContext';
import { TooltipPortalContext } from './TooltipPortalContext';
import customEventHandler from '../helper/customEvents';
import { canUndo, canRedo } from '../helper/stateManagerHelper';
import useResizeObserver from '../helper/hooks/useResizeObserver';
import { t } from 'i18next';
import { useRovingTabIndex } from '../helper/hooks/useRovingTabIndex';
import { getImageSrc } from '../helper/imageBlobCache';
export var Header = function () {
    var _a, _b, _c, _d;
    var _e = useContext(DataContext), drawCommands = _e.drawCommands, annotations = _e.annotations, text = _e.text, table = _e.table, shades = _e.shades, manipulative = _e.manipulative;
    // Reset is enabled whenever the whiteboard has any user-placed content —
    // shapes, manipulatives, counters, draw commands, text, tables, or shades.
    // An empty whiteboard means there's nothing to reset, so the button stays
    // disabled regardless of any latent history entries.
    var hasWhiteboardContent = drawCommands.length > 0 ||
        annotations.length > 0 ||
        text.length > 0 ||
        table.length > 0 ||
        shades.length > 0 ||
        manipulative.length > 0;
    var mainConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).mainConfig;
    var _f = globalStore.useStoreState(function (state) { return state.playerStore; }), isIDM = _f.isIDM, isSwp = _f.isSwp, language = _f.language;
    var uiData = mainConfig === null || mainConfig === void 0 ? void 0 : mainConfig.headerBtn;
    var elementRef = useResizeObserver().elementRef;
    // Track which buttons should be disabled
    var _g = useState([]), disabledButtons = _g[0], setDisabledButtons = _g[1];
    // Track logo hover state for tooltip
    var _h = useState(false), logoHovered = _h[0], setLogoHovered = _h[1];
    // Function to update button states
    var updateButtonStates = useCallback(function () {
        var disabled = [];
        if (!canUndo()) {
            disabled.push('undo');
        }
        if (!canRedo()) {
            disabled.push('redo');
        }
        // Reset is gated on whiteboard content, not on history. An empty
        // whiteboard with no shapes / manipulatives / draw commands has
        // nothing to reset even if undoable history exists.
        if (!hasWhiteboardContent) {
            disabled.push('reset');
        }
        setDisabledButtons(disabled);
    }, [hasWhiteboardContent]);
    // Update button states on mount and when state changes.
    // Defer initial update to avoid "setState during render" when the
    // context provider and consumers mount in the same React commit.
    useEffect(function () {
        // Defer so the first call doesn't overlap with the parent render commit
        var initialTimer = setTimeout(updateButtonStates, 0);
        var handleStateChange = function () {
            updateButtonStates();
        };
        customEventHandler.addEventListener('stateChanged', handleStateChange);
        customEventHandler.addEventListener('headerClicked', handleStateChange);
        customEventHandler.addEventListener('sidebarClicked', handleStateChange);
        return function () {
            clearTimeout(initialTimer);
            customEventHandler.removeEventListener('stateChanged', handleStateChange);
            customEventHandler.removeEventListener('headerClicked', handleStateChange);
            customEventHandler.removeEventListener('sidebarClicked', handleStateChange);
        };
    }, [updateButtonStates]);
    var leftCount = (_b = (_a = uiData === null || uiData === void 0 ? void 0 : uiData.headerLeft) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    var rightCount = (_d = (_c = uiData === null || uiData === void 0 ? void 0 : uiData.headerRight) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    // Two separate groups: headerLeft and headerRight, each its own tab stop
    var leftRoving = useRovingTabIndex({ itemCount: leftCount, orientation: 'horizontal' });
    var rightRoving = useRovingTabIndex({ itemCount: rightCount, orientation: 'horizontal' });
    var onHeaderClick = useCallback(function (id, type, data) {
        customEventHandler.dispatchEvent('headerClicked', { id: id, type: type, data: data });
        if (id === 'undo' || id === 'redo' || id === 'reset') {
            setTimeout(updateButtonStates, 0);
        }
    }, [updateButtonStates]);
    return (_jsx(_Fragment, { children: uiData !== undefined && (_jsxs("header", { className: "appHeader", ref: elementRef, 
            // role="banner"
            tabIndex: -1, children: [_jsx("div", { className: "logoHolder", onMouseOver: function () { return setLogoHovered(true); }, onMouseOut: function () { return setLogoHovered(false); }, children: _jsx("h1", { className: "logoTitle", "aria-label": "".concat(t('text.appTitle'), " ").concat(t('text.appTitleSuffix')), children: (uiData === null || uiData === void 0 ? void 0 : uiData.logo) && (_jsx("img", { src: language === 'es' ? getImageSrc('./assets/images/logoNewES.png') : getImageSrc(uiData.logo), alt: "", className: "headerLogo", "aria-hidden": "true" })) }) }), _jsx(TooltipPortalContext.Provider, { value: true, children: _jsxs("div", { className: "contentWrapper", children: [_jsx(CustomScrollbar, { className: "headerLeft", direction: "horizontal", style: { flex: '1 1 auto', minWidth: 0, width: 'auto', height: '100%' }, children: _jsx("div", __assign({ role: "toolbar", "aria-label": t('text.editActions'), "aria-orientation": "horizontal" }, leftRoving.groupProps, { children: _jsx(ButtonGroup, { align: "horizontal", buttonsInfo: uiData.headerLeft, mainClass: "headerLeft", type: "headerBtn", isParentRelative: true, disabledButtons: disabledButtons, rovingGetTabIndex: leftRoving.getTabIndex, rovingSetItemRef: leftRoving.setItemRef, rovingStartIndex: 0, onClick: onHeaderClick, useViewportPosition: true, collapsible: {
                                            isTooltipRelative: true,
                                            isRelative: true,
                                        } }) })) }), _jsxs("div", __assign({ className: "headerRight1", role: "toolbar", "aria-label": t('text.fileActions'), "aria-orientation": "horizontal" }, rightRoving.groupProps, { children: [(isIDM || isSwp) && (_jsx(CustomScrollbar, { className: "headerNavScroll", direction: "horizontal", style: { flex: '0 1 auto', minWidth: 0, width: 'auto', height: '100%' }, children: _jsx(NavBtn, { class: "headerNav", type: "header" }) })), _jsx(CustomScrollbar, { className: "headerRightScroll", direction: "horizontal", style: { flex: '0 1 auto', minWidth: 0, width: 'auto', height: '100%' }, children: _jsx(ButtonGroup, { align: "horizontal", buttonsInfo: uiData.headerRight, mainClass: "headerRight", type: "headerBtn", isParentRelative: true, disabledButtons: disabledButtons, rovingGetTabIndex: rightRoving.getTabIndex, rovingSetItemRef: rightRoving.setItemRef, rovingStartIndex: 0, onClick: onHeaderClick }) })] }))] }) })] })) }));
};
export default Header;
